import React, { useState, useEffect } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  BarChart3,
  TrendingUp,
  Search,
  MapPin,
  Music,
  Calendar,
  Users,
  Trash2,
  RefreshCw,
  Filter,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function StatistikPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [filterMode, setFilterMode] = useState("days"); // 'days' | 'custom'
  const [days, setDays] = useState(7);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      let url = "/analytics/stats";
      if (filterMode === "custom" && startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      } else {
        url += `?days=${days}`;
      }
      const res = await api.get(url);
      setStats(res.data);
    } catch (e) {
      toast.error("Statistiken konnten nicht geladen werden");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilter = () => {
    if (filterMode === "custom" && (!startDate || !endDate)) {
      toast.error("Bitte Von- und Bis-Datum wählen");
      return;
    }
    fetchStats();
  };

  const handleReset = async () => {
    try {
      const res = await api.delete("/analytics/reset");
      toast.success(`${res.data.deleted_count} Events gelöscht`);
      fetchStats();
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Nur Super-Admins können Statistiken löschen");
      } else {
        toast.error("Zurücksetzen fehlgeschlagen");
      }
    }
  };

  const checkDuplicates = async () => {
    try {
      const res = await api.get("/analytics/duplicates");
      setDuplicateInfo(res.data);
    } catch (e) {
      toast.error("Duplikate konnten nicht geprüft werden");
    }
  };

  const handleCleanup = async () => {
    setCleanupLoading(true);
    try {
      const res = await api.post("/analytics/cleanup-duplicates");
      const compactInfo = res.data.compacted ? " Speicher freigegeben." : "";
      toast.success(
        `${res.data.deleted_count} Duplikate entfernt. ${res.data.remaining_events} echte Events übrig.${compactInfo}`
      );
      setDuplicateInfo(null);
      fetchStats();
    } catch (e) {
      toast.error("Bereinigung fehlgeschlagen");
    }
    setCleanupLoading(false);
  };

  if (loading && !stats) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!stats) return null;

  const maxInstrumentCount = stats.top_instruments[0]?.count || 1;
  const maxGemeindeCount = stats.top_gemeinden[0]?.count || 1;
  const maxDailyCount = Math.max(...stats.daily_breakdown.map((d) => d.count), 1);

  const successRateColor =
    stats.success_rate >= 75
      ? "text-green-600"
      : stats.success_rate >= 50
      ? "text-yellow-600"
      : "text-red-600";

  return (
    <div className="space-y-6 p-6" data-testid="statistik-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-[#cf2e2e]" />
            Statistik
          </h1>
          <p className="text-gray-600 mt-1">
            Anonyme Nutzungsdaten der Wizard-Seite
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilter(!showFilter)}
            data-testid="filter-toggle-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button
            variant="outline"
            onClick={fetchStats}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>

          <AlertDialog onOpenChange={(open) => { if (open) checkDuplicates(); }}>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                data-testid="cleanup-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Bereinigen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Duplikate bereinigen?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-left">
                    <p>
                      Dieser Vorgang entfernt nur <strong>echte Duplikate</strong> –
                      Events vom gleichen Typ, die innerhalb von 2 Sekunden
                      mehrfach gespeichert wurden (verursacht durch einen frühen
                      Tracking-Bug). Im Anschluss wird Speicherplatz freigegeben.
                    </p>
                    {duplicateInfo ? (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                        <p className="font-medium text-blue-900">Vorschau:</p>
                        <ul className="text-sm text-blue-800 mt-1 space-y-1">
                          <li>
                            Aktuelle Events: <strong>{duplicateInfo.total_events}</strong>
                          </li>
                          <li>
                            Duplikate erkannt: <strong>{duplicateInfo.duplicate_count}</strong>
                          </li>
                          <li>
                            Nach Bereinigung: <strong>{duplicateInfo.events_after_cleanup}</strong>
                          </li>
                        </ul>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Lade Vorschau...</p>
                    )}
                    <p className="text-sm text-gray-600 mt-2">
                      Echte Statistik-Daten (Top-Instrumente, Gemeinden, etc.)
                      bleiben unverändert erhalten.
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleCleanup}
                  disabled={cleanupLoading || !duplicateInfo || duplicateInfo.duplicate_count === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="cleanup-confirm-btn"
                >
                  {cleanupLoading ? "Bereinige..." : "Ja, bereinigen"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="reset-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Zurücksetzen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Statistik zurücksetzen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Alle Event-Daten werden unwiderruflich gelöscht. Diese Aktion
                  kann nicht rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleReset}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="reset-confirm-btn"
                >
                  Ja, zurücksetzen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilter && (
        <Card className="p-6" data-testid="filter-panel">
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="min-w-[200px]">
                <Label>Zeitraum</Label>
                <Select
                  value={filterMode === "days" ? String(days) : "custom"}
                  onValueChange={(v) => {
                    if (v === "custom") {
                      setFilterMode("custom");
                    } else {
                      setFilterMode("days");
                      setDays(Number(v));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Letzte 7 Tage</SelectItem>
                    <SelectItem value="14">Letzte 14 Tage</SelectItem>
                    <SelectItem value="30">Letzte 30 Tage</SelectItem>
                    <SelectItem value="90">Letzte 90 Tage</SelectItem>
                    <SelectItem value="365">Letztes Jahr</SelectItem>
                    <SelectItem value="custom">Benutzerdefiniert</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterMode === "custom" && (
                <>
                  <div>
                    <Label>Von</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div>
                    <Label>Bis</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      data-testid="end-date-input"
                    />
                  </div>
                </>
              )}

              <Button onClick={handleApplyFilter} data-testid="apply-filter-btn">
                Anwenden
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Wizard-Aufrufe</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {stats.wizard_starts}
              </p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Erfolgreiche Suchen</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {stats.searches_completed}
              </p>
            </div>
            <Search className="w-8 h-8 text-green-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Keine Ergebnisse</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">
                {stats.no_results}
              </p>
            </div>
            <Calendar className="w-8 h-8 text-orange-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Erfolgsrate</p>
              <p className={`text-3xl font-bold mt-1 ${successRateColor}`}>
                {stats.success_rate}%
              </p>
            </div>
            <TrendingUp className={`w-8 h-8 ${successRateColor}`} />
          </div>
        </Card>
      </div>

      {/* Top Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Instruments */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Music className="w-5 h-5 text-[#cf2e2e]" />
            Meistgesuchte Instrumente
          </h2>
          {stats.top_instruments.length === 0 ? (
            <p className="text-gray-400 text-sm">Noch keine Daten vorhanden</p>
          ) : (
            <div className="space-y-3">
              {stats.top_instruments.map((item, idx) => (
                <div key={item.name} className="flex items-center gap-3">
                  <div
                    className={`w-8 text-center font-bold ${
                      idx === 0
                        ? "text-yellow-600"
                        : idx === 1
                        ? "text-gray-500"
                        : idx === 2
                        ? "text-orange-600"
                        : "text-gray-400"
                    }`}
                  >
                    {idx + 1}.
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-gray-600 text-sm">{item.count}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#cf2e2e] rounded-full transition-all"
                        style={{
                          width: `${(item.count / maxInstrumentCount) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Top Gemeinden */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#cf2e2e]" />
            Meistgesuchte Gemeinden
          </h2>
          {stats.top_gemeinden.length === 0 ? (
            <p className="text-gray-400 text-sm">Noch keine Daten vorhanden</p>
          ) : (
            <div className="space-y-3">
              {stats.top_gemeinden.map((item, idx) => (
                <div key={item.name} className="flex items-center gap-3">
                  <div
                    className={`w-8 text-center font-bold ${
                      idx === 0
                        ? "text-yellow-600"
                        : idx === 1
                        ? "text-gray-500"
                        : idx === 2
                        ? "text-orange-600"
                        : "text-gray-400"
                    }`}
                  >
                    {idx + 1}.
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-gray-600 text-sm">{item.count}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#cf2e2e] rounded-full transition-all"
                        style={{
                          width: `${(item.count / maxGemeindeCount) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Daily Chart */}
      {stats.daily_breakdown.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#cf2e2e]" />
            Tägliche Aufrufe
          </h2>
          <div className="flex items-end gap-2 h-40 overflow-x-auto">
            {stats.daily_breakdown.map((day) => (
              <div
                key={day.date}
                className="flex flex-col items-center gap-1 min-w-[40px] group relative"
              >
                <div
                  className="w-full bg-[#cf2e2e] rounded-t transition-all hover:bg-[#b52626]"
                  style={{
                    height: `${(day.count / maxDailyCount) * 100}%`,
                    minHeight: "4px",
                  }}
                />
                <span className="text-xs text-gray-500 rotate-45 origin-left whitespace-nowrap mt-1">
                  {day.date.substring(5)}
                </span>
                {/* Tooltip */}
                <div className="absolute bottom-full mb-2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  {day.date}: {day.count}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Info Card */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <p className="text-sm text-blue-900">
          <strong>Datenschutz:</strong> Es werden ausschließlich anonyme
          Nutzungsdaten erfasst. Keine IP-Adressen, keine persönlichen Daten.
          Zeitraum: <strong>{stats.period_days} Tage</strong>, insgesamt{" "}
          <strong>{stats.total_events} Events</strong>.
        </p>
      </Card>
    </div>
  );
}
